
import React from "react";
import WorkflowsSidebar from "../WorkflowsSidebar";
import ActionItemsSidebar from "../ActionItemsSidebar";
import { QuickActionsPanel } from "../QuickActionsPanel";
import { AISettings } from "@/components/settings/AISettings";
import { BackgroundJobsPanel } from "./BackgroundJobsPanel";
import { ActiveSidebar } from "../../types/chat-sidebar-types";
import { Conversation } from "../../hooks/useChat";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X, Loader2, Brain, MessageSquare, Trash2 } from "lucide-react";
import { cn } from '@flowstate/utils';
import { getTouchButtonClasses } from "@/utils/touchTargets";
import { getConversationIcon } from "../../utils/chatUtils";
import type { ConversationTodoList } from "../../types/chat";

// Note: Many imports are missing here from original ChatSidebar (SystemState, etc)
// We need to pass down a lot of props or use context.
// For now, let's keep it somewhat monolithic in props to avoid massive refactoring of data flow,
// but extract the rendering logic.
// Actually, SidebarNavigation is confusingly named. 
// The original plan called this "SidebarNavigation" but the content is actually 
// "SidebarContent" or "SecondarySidebars".
// The outline says: "Renders the side tabs (Cortex, Workflows, Quick Actions)."
// But the tabs triggers are in the Header. 
// So this component should render the *content* of the sidebars.

interface SidebarNavigationProps {
  activeSidebar: ActiveSidebar | null;
  setActiveSidebar: (sidebar: ActiveSidebar | null) => void;
  isMobile: boolean;
  // Cortex props
  // Workflows props
  onWorkflowRun: (workflowId: string) => void;
  // Action Items props
  allActionItems: any[]; // Type should be imported
  handleActionItemsUpdate: (items: any[]) => void;
  // Quick Actions props
  executeQuickAction: (action: string) => void;
  // Agent List props
  conversationList: Conversation[];
  currentConversationId: string | null;
  startNewConversation: () => void;
  loadConversation: (id: string) => void;
  deleteConversation: (id: string) => void;
  selectedContextIds: string[];
  toggleContextSelection: (id: string) => void;
  summarizeSelectedConversations: () => void;
  isSummarizing: boolean;
  summarizedContext: string | null;
  updatedTitles: Record<string, string>;
  
  // Tooltip state for agent list (from original)
  selectedConversationId: string | null;
  setSelectedConversationId: (id: string | null) => void;
  tooltipPosition: { top: number; left: number } | null;
  setTooltipPosition: (pos: { top: number; left: number } | null) => void;
  handleConversationClick: (id: string, title: string, event: React.MouseEvent) => void;
}

export function SidebarNavigation({
  activeSidebar,
  setActiveSidebar,
  isMobile,
  onWorkflowRun,
  allActionItems,
  handleActionItemsUpdate,
  executeQuickAction,
  conversationList,
  currentConversationId,
  startNewConversation,
  loadConversation,
  deleteConversation,
  selectedContextIds,
  toggleContextSelection,
  summarizeSelectedConversations,
  isSummarizing,
  summarizedContext,
  updatedTitles,
  selectedConversationId: _selectedConversationId,
  setSelectedConversationId: _setSelectedConversationId,
  tooltipPosition: _tooltipPosition,
  setTooltipPosition: _setTooltipPosition,
  handleConversationClick: _handleConversationClick
}: SidebarNavigationProps) {

  return (
    <>
      {/* Agents List Sidebar */}
      {activeSidebar === 'chat' && (
        <div className="absolute inset-0 z-20" data-testid="cortex-agents-panel">
           <div className="absolute inset-0 z-20 bg-background/60 backdrop-blur-xl border-r border-border/20 flex flex-col rounded-xl">
            {/* Header with close button */}
            <div className="flex items-center justify-between p-4 border-b border-border/20 bg-background/40 backdrop-blur-sm rounded-t-xl">
              <h2 className="text-lg font-semibold">AI Agents</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setActiveSidebar(null)}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* New Agent Button */}
            <div className="p-2 border-b flex flex-col gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className={cn(
                  "w-full justify-start",
                  isMobile && getTouchButtonClasses('medium')
                )}
                onClick={startNewConversation}
              >
                <Plus className="mr-2 h-4 w-4" /> New Agent Chat
              </Button>
              
              {/* Context Selection Header */}
              {conversationList.length > 0 && (
                <div className="flex flex-col gap-2 mt-2 p-2 bg-secondary/30 rounded-md border border-secondary">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Context ({selectedContextIds.length})</span>
                    {summarizedContext && (
                      <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" title="Context Active" />
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    variant={selectedContextIds.length > 0 ? "default" : "outline"}
                    className="h-7 text-[10px] gap-1"
                    disabled={selectedContextIds.length === 0 || isSummarizing}
                    onClick={summarizeSelectedConversations}
                  >
                    {isSummarizing ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Brain className="h-3 w-3" />
                    )}
                    Update Context
                  </Button>
                </div>
              )}
            </div>
            
            {/* Conversation List */}
            <div className="flex-1 overflow-auto p-2 space-y-1">
              {conversationList.map((conv, _index) => {
                const displayTitle = updatedTitles[conv.id] || conv.title;
                // Need to import/define getConversationIcon logic or pass it down?
                // Importing from utils is better if possible.
                // Assuming it's in utils now or handled by parent?
                // Original code had helper function inside component or imported?
                // It was imported from somewhere or defined in file. 
                // Outline showed it as function in file. I need to move it to utils or include it here.
                // Let's assume passed in or I'll implement a simple version/import if exists.
                // Checking previous outline... `getConversationIcon` was a helper function signature.
                // I will add it to `chatUtils.ts` if not there, or copy it here.
                
                // For now, simple fallback icon
                const conversationIcon = <MessageSquare className="h-4 w-4" />; 

                const isSelected = selectedContextIds.includes(conv.id);
                
                return (
                 <div key={conv.id} className="group relative flex items-center w-full gap-2">
                    <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleContextSelection(conv.id)}
                        className="h-4 w-4 shrink-0 transition-opacity opacity-40 group-hover:opacity-100 data-[state=checked]:opacity-100"
                    />
                    <div className="relative flex-1 min-w-0">
                        <Button 
                        variant={conv.id === currentConversationId ? "secondary" : "ghost"}
                        size="sm"
                        className="w-full justify-start text-left truncate pr-10 h-auto py-2"
                        onClick={() => {
                          loadConversation(conv.id);
                          setActiveSidebar(null);
                        }}
                        >
                        {conversationIcon}
                        <span className="truncate ml-2">{displayTitle}</span>
                        </Button>
                        <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity z-10 hover:bg-destructive/10"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (confirm('Are you sure you want to delete this conversation?')) {
                            deleteConversation(conv.id);
                            }
                        }}
                        title="Delete conversation"
                        >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                        </Button>
                    </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Workflows Sidebar */}
      {activeSidebar === 'workflows' && (
        <div className="absolute inset-0 z-20" data-testid="cortex-workflows-panel">
          <WorkflowsSidebar
            onWorkflowRun={onWorkflowRun}
            onClose={() => setActiveSidebar(null)}
            isMobile={isMobile}
          />
        </div>
      )}

      {/* Action Items Sidebar */}
      {activeSidebar === 'action-items' && (
        <div className="absolute inset-0 z-20" data-testid="cortex-action-items-panel">
          <ActionItemsSidebar
            items={allActionItems}
            onUpdate={(todoList: ConversationTodoList) => handleActionItemsUpdate(todoList.items)}
            onClose={() => setActiveSidebar(null)}
            isMobile={isMobile}
          />
        </div>
      )}

      {/* Quick Actions Sidebar */}
      {activeSidebar === 'quick-actions' && (
        <div className="absolute inset-0 z-20" data-testid="cortex-quick-actions-panel">
          <div className={cn(
            "bg-background/60 backdrop-blur-xl border-l border-border/20 flex flex-col h-full rounded-xl",
            isMobile && "absolute inset-0 z-20 w-full"
          )}>
            <QuickActionsPanel
              onActionExecute={executeQuickAction}
              onClose={() => setActiveSidebar(null)}
            />
          </div>
        </div>
      )}
      {/* Agent Settings Sidebar */}
      {activeSidebar === 'settings' && (
        <div className="absolute inset-0 z-20" data-testid="cortex-settings-panel">
          <div className={cn(
            "bg-background/60 backdrop-blur-xl border-l border-border/20 flex flex-col h-full rounded-xl overflow-y-auto",
            isMobile && "absolute inset-0 z-20 w-full"
          )}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/20">
              <h3 className="font-semibold text-sm">Agent Settings</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setActiveSidebar(null)}
                className="h-8 w-8"
                aria-label="Close settings"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <AISettings />
            </div>
          </div>
        </div>
      )}
      {/* Background Jobs Panel */}
      {activeSidebar === 'background-jobs' && (
        <div className="absolute inset-0 z-20" data-testid="cortex-background-jobs-panel">
          <div className={cn(
            "bg-background/60 backdrop-blur-xl border-l border-border/20 flex flex-col h-full rounded-xl overflow-hidden",
            isMobile && "absolute inset-0 z-20 w-full"
          )}>
            <BackgroundJobsPanel />
          </div>
        </div>
      )}
    </>
  );
}
